import { brandLabels, brands, BrandType } from 'noddi-types';

const MIN_LABEL_PREFIX_LENGTH = 4;

/**
 * Matches a department name to a brand type using case-insensitive comparison.
 *
 * The function extracts the first word from the department name and matches it against:
 * 1. Brand slugs (e.g., "carglass", "bestdrive")
 * 2. Brand labels (e.g., "Hurtigruta carglass", "BestDrive")
 *
 * @param departmentName - The full department name (e.g., "Hurtigruta Oslo", "BestDrive Trondheim")
 * @returns The matching BrandType or null if no match found
 *
 * @example
 * ```typescript
 * matchDepartmentToBrand("Hurtigruta Oslo") // returns brands.carglass
 * matchDepartmentToBrand("bestdrive bergen") // returns brands.bestdrive
 * matchDepartmentToBrand("Trønderdekk Stavanger") // returns brands.tronderdekk
 * matchDepartmentToBrand("Unknown Department") // returns null
 * ```
 */
export function matchDepartmentToBrand(departmentName?: string): BrandType | null {
  if (!departmentName) {
    return null;
  }

  // Convert to lowercase and extract the first word (department prefix)
  // Handle special characters by splitting on common separators first
  const normalizedName = departmentName.toLowerCase().replace(/[-_]/g, ' ');
  const departmentPrefix = normalizedName.split(' ')[0];

  if (!departmentPrefix) {
    return null;
  }

  // Check against all brand types
  for (const [brandKey, brandSlug] of Object.entries(brands)) {
    const brandType = brandKey as BrandType;

    // Check if department prefix contains the brand slug
    if (departmentPrefix.includes(brandSlug)) {
      return brandType;
    }

    // Check if brand label contains the department prefix (case-insensitive). Short prefixes are
    // skipped: "My Oslo" is contained in "Myldre" and "Lot Bergen" in "Lotus".
    const brandLabel = brandLabels[brandType];
    if (
      departmentPrefix.length >= MIN_LABEL_PREFIX_LENGTH &&
      brandLabel &&
      brandLabel.toLowerCase().includes(departmentPrefix)
    ) {
      return brandType;
    }
  }

  return null;
}

/**
 * Detects the brand type based on the current location (pathname and hostname).
 * Checks both pathname and hostname for each brand and returns the first match.
 * Falls back to the default noddi brand if no match is found.
 *
 * @param pathname - The current pathname (e.g., "/bestdrive/dashboard")
 * @param hostname - The current hostname (e.g., "bestdrive.noddi.com")
 * @returns The detected BrandType
 *
 * @example
 * ```typescript
 * detectBrandFromLocation("/bestdrive/dashboard", "bestdrive.noddi.com") // returns brands.bestdrive
 * detectBrandFromLocation("/carglass/profile", "noddi.com") // returns brands.carglass
 * detectBrandFromLocation("/dashboard", "noddi.com") // returns brands.noddi (default)
 * ```
 */
export function detectBrandFromLocation(pathname: string, hostname: string): BrandType {
  // Check both pathname and hostname for each partner
  if (pathname.includes(brands.bestdrive) || hostname.includes(brands.bestdrive)) {
    return brands.bestdrive;
  }

  if (pathname.includes(brands.tronderdekk) || hostname.includes(brands.tronderdekk)) {
    return brands.tronderdekk;
  }

  if (pathname.includes(brands.carglass) || hostname.includes(brands.carglass)) {
    return brands.carglass;
  }

  if (pathname.includes(brands.dekkstra) || hostname.includes(brands.dekkstra)) {
    return brands.dekkstra;
  }

  if (pathname.includes(brands.dekkfix) || hostname.includes(brands.dekkfix)) {
    return brands.dekkfix;
  }

  if (pathname.includes(brands.elitebilvask) || hostname.includes(brands.elitebilvask)) {
    return brands.elitebilvask;
  }

  if (pathname.includes(brands.dekkteam) || hostname.includes(brands.dekkteam)) {
    return brands.dekkteam;
  }

  if (pathname.includes(brands.shine) || hostname.includes(brands.shine)) {
    return brands.shine;
  }

  if (pathname.includes(brands.lotus) || hostname.includes(brands.lotus)) {
    return brands.lotus;
  }

  if (pathname.includes(brands.myldre) || hostname.includes(brands.myldre)) {
    return brands.myldre;
  }

  if (pathname.includes(brands.meks) || hostname.includes(brands.meks)) {
    return brands.meks;
  }

  return brands.noddi;
}
